// RevenueCat purchase adapter — implements PurchasePort.
//
// Maps the RevenueCat customer/entitlement model onto our PurchasePort:
//   - active entitlement ids  = Object.keys(customerInfo.entitlements.active)
//   - identify(userId)        = Purchases.logIn({ appUserID })
//   - purchase(tier, period)  = find offering `tier` -> package for `period`
//                               -> purchasePackage -> new active entitlements
//   - restore()               = restorePurchases -> active entitlements
//   - onEntitlementsChange    = addCustomerInfoUpdateListener
//
// Dashboard convention (documented in 04-BACKEND-RUNBOOK): one RevenueCat
// *offering* per paid tier (identifier === tier), each exposing a MONTHLY and an
// ANNUAL package, and one *entitlement* per tier (identifier === tier). The
// package resolver is injectable so this stays adaptable + unit-testable.

import type { PurchasePort } from '../bridge/ports';
import type { Tier } from '../domain/types';

// ---- narrow structural view of the RevenueCat SDK we actually use -----------

interface RcEntitlementInfos {
  active: Record<string, unknown>;
}
interface RcCustomerInfo {
  entitlements: RcEntitlementInfos;
}
interface RcPackage {
  identifier: string;
  packageType: string; // 'MONTHLY' | 'ANNUAL' | ...
}
interface RcOffering {
  identifier: string;
  availablePackages: RcPackage[];
}
interface RcOfferings {
  current: RcOffering | null;
  all: Record<string, RcOffering>;
}

export interface RevenueCatLike {
  getCustomerInfo(): Promise<{ customerInfo: RcCustomerInfo }>;
  logIn(opts: { appUserID: string }): Promise<{ customerInfo: RcCustomerInfo }>;
  getOfferings(): Promise<RcOfferings>;
  purchasePackage(opts: { aPackage: RcPackage }): Promise<{ customerInfo: RcCustomerInfo }>;
  restorePurchases(): Promise<{ customerInfo: RcCustomerInfo }>;
  addCustomerInfoUpdateListener(cb: (info: RcCustomerInfo) => void): Promise<string> | string;
  removeCustomerInfoUpdateListener?(callbackId: string): Promise<void> | void;
}

function activeEntitlementIds(info: RcCustomerInfo): string[] {
  return Object.keys(info.entitlements?.active ?? {});
}

function periodToPackageType(period: 'monthly' | 'annual'): string {
  return period === 'annual' ? 'ANNUAL' : 'MONTHLY';
}

export class RevenueCatPurchase implements PurchasePort {
  constructor(private readonly rc: RevenueCatLike) {}

  async getActiveEntitlements(): Promise<string[]> {
    const { customerInfo } = await this.rc.getCustomerInfo();
    return activeEntitlementIds(customerInfo);
  }

  async identify(userId: string): Promise<void> {
    if (!userId) return; // anonymous Supabase user with empty id -> skip
    await this.rc.logIn({ appUserID: userId });
  }

  async purchase(tier: Tier, period: 'monthly' | 'annual'): Promise<string[]> {
    if (tier === 'free') {
      // Nothing to buy; return whatever is currently active.
      return this.getActiveEntitlements();
    }
    const pkg = await this.resolvePackage(tier, period);
    const { customerInfo } = await this.rc.purchasePackage({ aPackage: pkg });
    return activeEntitlementIds(customerInfo);
  }

  async restore(): Promise<string[]> {
    const { customerInfo } = await this.rc.restorePurchases();
    return activeEntitlementIds(customerInfo);
  }

  onEntitlementsChange(cb: (entitlementIds: string[]) => void): () => void {
    let callbackId: string | null = null;
    void Promise.resolve(
      this.rc.addCustomerInfoUpdateListener((info) => cb(activeEntitlementIds(info))),
    ).then((id) => {
      callbackId = id;
    });
    return () => {
      if (callbackId && this.rc.removeCustomerInfoUpdateListener) {
        void this.rc.removeCustomerInfoUpdateListener(callbackId);
      }
    };
  }

  /** Find the package for a tier+period via the dashboard convention. */
  private async resolvePackage(tier: Tier, period: 'monthly' | 'annual'): Promise<RcPackage> {
    const offerings = await this.rc.getOfferings();
    const offering =
      offerings.all[tier] ??
      (offerings.current?.identifier === tier ? offerings.current : undefined);
    if (!offering) {
      throw new Error(`revenuecat: no offering for tier '${tier}'`);
    }
    const wanted = periodToPackageType(period);
    const pkg = offering.availablePackages.find((p) => p.packageType === wanted);
    if (!pkg) {
      throw new Error(`revenuecat: no '${period}' package in offering '${tier}'`);
    }
    return pkg;
  }
}
